import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
});

// Add auth token and Content-Type to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('stellar_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Only set Content-Type to JSON if not already FormData
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  // 2026-06-16 — View-only mode hard gate.
  // D94 / 2026-02-24 — DISABLED PER USER DIRECTIVE while attendance is
  // rolled back. Every authenticated user must be able to write to the
  // CRM regardless of clock-in state. The interceptor still examines
  // the flag for forward-compat, but ClockGateProvider is disabled in
  // TaskManagerShell so __STELLAR_VIEW_ONLY__ never gets set to true.
  // We additionally force-clear it on every request so that any stale
  // value from a prior session can't accidentally block writes.
  if (typeof window !== 'undefined') {
    window.__STELLAR_VIEW_ONLY__ = false;
  }
  const method = (config.method || 'get').toLowerCase();
  const url = config.url || '';
  const isWrite = !['get', 'head', 'options'].includes(method);
  const isClockGateEscape =
    /\/clock\/(v2\/)?(in|out|not-working-today)/.test(url) ||
    url.startsWith('/push/');
  if (isWrite && window.__STELLAR_VIEW_ONLY__ === true && !isClockGateEscape) {
    const err = new Error('view_only_blocked');
    err.__view_only_blocked__ = true;
    return Promise.reject(err);
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // View-only block — fired pre-flight by request interceptor.
    if (error?.__view_only_blocked__) {
      try {
        import('sonner').then(({ toast }) => {
          toast.error("You're in view-only mode. Clock in to make changes.", {
            duration: 5000,
            action: {
              label: 'Clock In',
              onClick: () => {
                try { window.dispatchEvent(new Event('stellar-open-clock-in-modal')); } catch { /* noop */ }
              },
            },
          });
        });
      } catch { /* noop */ }
      return Promise.reject(error);
    }
    // Iter 68 C5: skip the global 401-redirect for endpoints where a 401 is a
    // legitimate "wrong credentials" outcome the calling page must surface itself
    // (otherwise the redirect wipes the inline error before the user ever sees it).
    const url = (error?.config?.url || '');
    const isAuthChallenge =
      /\/auth\/(login|register|forgot-password|accept-invite|validate-invite-token)/.test(url);
    if (error.response?.status === 401 && !isAuthChallenge) {
      localStorage.removeItem('stellar_token');
      localStorage.removeItem('stellar_user');
      window.location.href = '/login';
    }
    // 2026-06-09 — Global CLOCK_IN_REQUIRED gate. Backend signals with
    // `403 + { code: 'CLOCK_IN_REQUIRED', action, message }` whenever a
    // non-owner user attempts a "working on" action without an active
    // clock-in session. Surface a sonner toast with a 'Clock In Now'
    // action that dispatches a window event for TaskManagerShell to
    // catch and open its native clock-in confirm modal (so location
    // policy + EOD flow stays consistent). Other UI may also listen.
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'CLOCK_IN_REQUIRED') {
        try {
          // Lazy-load sonner so this module stays cheap.
          import('sonner').then(({ toast }) => {
            toast.error('You need to clock in before doing this.', {
              description: detail.action ? `Action: ${detail.action}` : undefined,
              duration: 8000,
              action: {
                label: 'Clock In Now',
                onClick: () => {
                  try {
                    window.dispatchEvent(new CustomEvent('stellar-clock-in-requested', { detail }));
                  } catch (_e) {}
                },
              },
            });
          });
        } catch (_e) {}
        // Mark the error so call sites can choose to silently swallow
        // (since the toast already explains it). Don't redirect.
        error.__clockInGate = true;
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  getMyWorkspaceRole: (workspaceId) => api.get(`/auth/me/role/${workspaceId}`),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  updateProfile: (data) => api.put('/auth/profile', data),
  // Batch 2B A10/A15/C12 — generic user-level preferences merger.
  patchPreferences: (prefs) => api.patch('/users/me/preferences', prefs),
  changePassword: (data) => api.post('/auth/change-password', data),
  acceptInvite: (token, password) => api.post('/auth/accept-invite', { token, password }),
  validateInviteToken: (token) => api.get(`/auth/validate-invite-token?token=${token}`),
  timezones: () => api.get('/timezones'),
};

// Workspaces
export const workspaceAPI = {
  list: () => api.get('/workspaces'),
  get: (id) => api.get(`/workspaces/${id}`),
  create: (data) => api.post('/workspaces', data),
  update: (id, data) => api.put(`/workspaces/${id}`, data),
  delete: (id) => api.delete(`/workspaces/${id}`),
  listMembers: (id) => api.get(`/workspaces/${id}/members`),
  // Phase 3A Follow-up — lightweight roster (id+name+email) for all roles.
  // Used by Team Chat composer/mention pills since /members is admin-gated.
  listMembersLite: (id) => api.get(`/workspaces/${id}/members-lite`),
  // 2026-06-05 PROD HOTFIX — assignee_picker_unscoped. Returns FULL roster
  // (same shape as /members) for ANY role. Task create + edit assignee
  // pickers use this so Manager / Team users see the whole team.
  // Activity-feed hierarchy is unaffected (separate endpoint).
  listAssignableMembers: (id) => api.get(`/workspaces/${id}/assignable-members`),
  // Phase 2 Group B4 — current user's direct reports (workspace_members
  // whose manager_id points at the caller). Returns `[user_id, ...]`.
  // Auth-scoped server-side; empty list when caller is not a Manager.
  myDirectReports: (id) => api.get(`/workspaces/${id}/my-direct-reports`),
  inviteMember: (id, data) => api.post(`/workspaces/${id}/members`, data),
  removeMember: (wsId, memberId) => api.delete(`/workspaces/${wsId}/members/${memberId}`),
};

// Media
export const mediaAPI = {
  upload: (workspaceId, file, onProgress, clientGroupId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('workspace_id', workspaceId);
    if (clientGroupId) formData.append('client_group_id', clientGroupId);
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  list: (workspaceId, skip = 0, limit = 50) => api.get(`/media/${workspaceId}?skip=${skip}&limit=${limit}`),
  download: (workspaceId, mediaId) => `${API_BASE}/media/${workspaceId}/${mediaId}/download`,
  delete: (workspaceId, mediaId) => api.delete(`/media/${workspaceId}/${mediaId}`),
};

// Posts
export const postAPI = {
  list: (workspaceId, status = null) => api.get(`/posts/${workspaceId}${status ? `?status=${status}` : ''}`),
  get: (workspaceId, postId) => api.get(`/posts/${workspaceId}/${postId}`),
  create: (data) => api.post('/posts', data),
  update: (workspaceId, postId, data) => api.put(`/posts/${workspaceId}/${postId}`, data),
  delete: (workspaceId, postId) => api.delete(`/posts/${workspaceId}/${postId}`),
};

// Approvals
export const approvalAPI = {
  list: (workspaceId, status = null) => api.get(`/approvals/${workspaceId}${status ? `?status=${status}` : ''}`),
  submit: (data) => api.post('/approvals', data),
  action: (approvalId, data) => api.put(`/approvals/${approvalId}`, data),
};

// Leaves (old API removed — see leaveAPI below)

// Google Drive
export const driveAPI = {
  connect: () => api.get('/auth/google-drive/connect'),
  status: () => api.get('/drive/status'),
  files: (query, folderId, pageToken) => api.get(`/drive/files?query=${query || ''}&folder_id=${folderId || 'root'}&page_token=${pageToken || ''}`),
  disconnect: () => api.get('/drive/disconnect'),
};

// Webhooks & Integrations

// OpenRouter AI
export const aiChatAPI = {
  models: () => api.get('/ai/models'),
  chat: (data) => api.post('/ai/chat', data),
  compare: (data) => api.post('/ai/compare', data),
  preferences: () => api.get('/ai/preferences'),
  updatePreferences: (data) => api.put('/ai/preferences', data),
  // Persistent sessions
  sessions: (workspaceId) => api.get(`/ai/sessions/${workspaceId}`),
  createSession: (workspaceId, data) => api.post(`/ai/sessions/${workspaceId}`, data),
  getSession: (workspaceId, sessionId) => api.get(`/ai/sessions/${workspaceId}/${sessionId}`),
  sendMessage: (workspaceId, sessionId, data) => api.post(`/ai/sessions/${workspaceId}/${sessionId}/message`, data),
  updateSession: (workspaceId, sessionId, data) => api.put(`/ai/sessions/${workspaceId}/${sessionId}`, data),
  deleteSession: (workspaceId, sessionId) => api.delete(`/ai/sessions/${workspaceId}/${sessionId}`),
  smartSummary: (workspaceId, sessionId, data) => api.post(`/ai/sessions/${workspaceId}/${sessionId}/smart-summary`, data),
  // D79 Phase 2 — Execute a confirmed action from the AI confirm card.
  executeAction: (workspaceId, payload) => api.post(`/ai/actions/${workspaceId}/execute`, payload),
  generateImage: (data) => api.post('/ai/generate-image', data),
  generateVideo: (data) => api.post('/ai/generate-video', data),
  videoStatus: (jobId) => api.get(`/ai/video-status/${jobId}`),
  videoDownload: (jobId) => `${API_BASE}/ai/video-download/${jobId}`,
  agents: (workspaceId) => api.get(`/ai/agents/${workspaceId}`),
  resolveAssignSuggestion: (workspaceId, taskId, action) => api.post(`/ai/agents/${workspaceId}/auto-assign/${taskId}/resolve`, { action }),
  enhanceText: (text, style) => api.post('/ai/enhance-text', { text, style }),
};

// Notion Import
export const notionAPI = {
  connect: (token) => api.post('/notion/connect', { token }),
  status: () => api.get('/notion/status'),
  search: (query, filterType) => api.get(`/notion/search?query=${encodeURIComponent(query || '')}&filter_type=${filterType || ''}`),
  databaseSchema: (dbId) => api.get(`/notion/database/${dbId}`),
  importData: (workspaceId, data) => api.post(`/notion/import/${workspaceId}`, data),
  importAll: (workspaceId) => api.post(`/notion/import-all/${workspaceId}`),
  importDeep: (workspaceId, projectId, data) => api.post(`/notion/import-deep/${workspaceId}/${projectId}`, data),
  disconnect: () => api.delete('/notion/disconnect'),
};


// Search
export const searchAPI = {
  search: (workspaceId, query) => api.get(`/search/${workspaceId}?q=${encodeURIComponent(query)}`),
};

// Notification Settings
export const notifSettingsAPI = {
  get: (workspaceId) => api.get(`/notification-settings/${workspaceId}`),
  update: (workspaceId, data) => api.put(`/notification-settings/${workspaceId}`, data),
};

// Timesheet
export const timesheetAPI = {
  get: (workspaceId, days) => api.get(`/timesheet/${workspaceId}?days=${days || 7}`),
  exportCsv: (workspaceId, days) => api.get(`/timesheet/${workspaceId}/export?days=${days || 7}`, { responseType: 'blob' }),
};

// Clock In/Out
export const clockAPI = {
  clockIn: (data) => api.post('/clock/in', data),
  clockOut: (data) => api.post('/clock/out', data),
  breakAction: (data) => api.post('/clock/break', data),
  status: () => api.get('/clock/status'),
  tasksToday: (workspaceId) => api.get(`/clock/tasks-today?workspace_id=${workspaceId}`),
  history: (workspaceId, days) => api.get(`/clock/history/${workspaceId}?days=${days || 30}`),
  eodReports: (workspaceId, days) => api.get(`/clock/eod-reports/${workspaceId}?days=${days || 7}`),
  eodDetail: (workspaceId, entryId) => api.get(`/clock/eod-reports/${workspaceId}/${entryId}`),
  eodPdfUrl: (workspaceId, entryId) => `${api.defaults.baseURL}/clock/eod-reports/${workspaceId}/${entryId}/pdf`,
  // 2026-06-06 — clock_out_paused_tasks resume banner endpoints.
  pausedTasks: () => api.get('/clock/paused-tasks'),
  dismissPausedTasks: () => api.post('/clock/paused-tasks/dismiss'),
  // 2026-02-23 — Admin/Owner/Manager override for stuck attendance.
  adminEndBreak: (workspaceId, targetUserId) =>
    api.post('/clock/admin/end-break', { workspace_id: workspaceId, target_user_id: targetUserId }),
  adminClockOut: (workspaceId, targetUserId, note) =>
    api.post('/clock/admin/clock-out', { workspace_id: workspaceId, target_user_id: targetUserId, note: note || '' }),
};

// 2026-06-16 — Authoritative attendance state. Frontend MUST call
// `attendanceAPI.status(workspaceId)` immediately before any clock-out,
// break-start, break-end, or EOD-submit action to fetch the server's truth.
// Never trust local clock state.
// 2026-02 — merged with the legacy `attendanceAPI.report` Owner-only call
// (admin_managed_leave + attendance_report) so consumers continue to work.
export const attendanceAPI = {
  status: (workspaceId) => api.get(`/attendance/status?workspace_id=${workspaceId}`),
  breakStart: (workspaceId) => api.post('/attendance/break-start', { workspace_id: workspaceId }),
  breakEnd: (workspaceId) => api.post('/attendance/break-end', { workspace_id: workspaceId }),
  // 2026-02-20 — S3 Break Flow: resume selected tasks after a break.
  breakResumeTasks: (workspaceId, taskIds) =>
    api.post('/attendance/break-resume-tasks', { workspace_id: workspaceId, task_ids: taskIds }),
  report: (workspaceId, params = {}) => {
    const q = new URLSearchParams();
    if (params.start_date) q.set('start_date', params.start_date);
    if (params.end_date) q.set('end_date', params.end_date);
    if (params.user_id) q.set('user_id', params.user_id);
    if (params.role) q.set('role', params.role);
    const qs = q.toString();
    return api.get(`/attendance/${workspaceId}/report${qs ? `?${qs}` : ''}`);
  },
  // 2026-02 — Owner/Admin manual correction for a missed clock-out.
  // Pass any subset of {clock_in_time, clock_out_time, note}; both
  // timestamps must be ISO-8601 (use new Date(localISO).toISOString()).
  correct: (payload) => api.patch('/attendance/correction', payload),
};

// 2026-02 — Attendance V2 (brand-new clock in/break/out system).
// Every action requires {workspace_id, local_date: 'YYYY-MM-DD',
// location: {lat, lng, accuracy, address}} — location is mandatory.
export const attendanceV2API = {
  clockIn: (payload) => api.post('/attendance-v2/clock-in', payload),
  breakStart: (payload) => api.post('/attendance-v2/break/start', payload),
  breakEnd: (payload) => api.post('/attendance-v2/break/end', payload),
  clockOut: (payload) => api.post('/attendance-v2/clock-out', payload),
  status: (workspaceId, localDate) =>
    api.get(`/attendance-v2/status?workspace_id=${workspaceId}&local_date=${localDate}`),
  sheet: (workspaceId, start, end, userId = '') =>
    api.get(`/attendance-v2/sheet?workspace_id=${workspaceId}&start=${start}&end=${end}${userId ? `&user_id=${userId}` : ''}`),
  summary: (workspaceId, start, end, userId = '') =>
    api.get(`/attendance-v2/summary?workspace_id=${workspaceId}&start=${start}&end=${end}${userId ? `&user_id=${userId}` : ''}`),
  requestCorrection: (recordId, payload) =>
    api.post(`/attendance-v2/records/${recordId}/corrections`, payload),
  pendingCorrections: (workspaceId) =>
    api.get(`/attendance-v2/corrections/pending?workspace_id=${workspaceId}`),
  approveCorrection: (recordId, editId, note = '') =>
    api.post(`/attendance-v2/corrections/${recordId}/${editId}/approve`, { note }),
  rejectCorrection: (recordId, editId, note = '') =>
    api.post(`/attendance-v2/corrections/${recordId}/${editId}/reject`, { note }),
  reportPdf: (workspaceId, start, end, userId = '', tzOffset = 0) =>
    api.get(`/attendance-v2/report/pdf?workspace_id=${workspaceId}&start=${start}&end=${end}${userId ? `&user_id=${userId}` : ''}&tz_offset=${tzOffset}`, { responseType: 'blob' }),
  tasksToContinue: (workspaceId) =>
    api.get(`/attendance-v2/tasks-to-continue?workspace_id=${workspaceId}`),
  resumeTask: (workspaceId, taskId) =>
    api.put(`/tasks/${workspaceId}/${taskId}`, { status: 'in_progress' }),
};

// 2026-02 — EOD V2 (rebuilt End-of-Day reports tied to Attendance V2).
export const eodV2API = {
  tasksTouched: (workspaceId, localDate, tzOffset = 0) =>
    api.get(`/eod-v2/tasks-touched?workspace_id=${workspaceId}&local_date=${localDate}&tz_offset=${tzOffset}`),
  todayStatus: (workspaceId, localDate) =>
    api.get(`/eod-v2/today-status?workspace_id=${workspaceId}&local_date=${localDate}`),
  submit: (payload) => api.post('/eod-v2/submit', payload),
  reports: (workspaceId, start, end, userId = '') =>
    api.get(`/eod-v2/reports?workspace_id=${workspaceId}&start=${start}&end=${end}${userId ? `&user_id=${userId}` : ''}`),
  members: (workspaceId) => api.get(`/eod-v2/members?workspace_id=${workspaceId}`),
  gaps: (workspaceId, start, end) =>
    api.get(`/eod-v2/gaps?workspace_id=${workspaceId}&start=${start}&end=${end}`),
  sendDigestNow: (workspaceId, date = '') =>
    api.post('/eod-v2/digest/send-now', { workspace_id: workspaceId, date }),
};

// 2026-06-06 — admin_managed_leave (Owner/Admin only).
export const adminLeaveAPI = {
  create: (workspaceId, payload) => api.post(`/availability/${workspaceId}/admin-leave`, payload),
  remove: (workspaceId, leaveId) => api.delete(`/availability/${workspaceId}/admin-leave/${leaveId}`),
  update: (workspaceId, leaveId, payload) => api.put(`/availability/${workspaceId}/admin-leave/${leaveId}`, payload),
  cancel: (workspaceId, leaveId) => api.put(`/availability/${workspaceId}/admin-leave/${leaveId}`, { action: 'cancel' }),
};

// 2026-06-07 — team_view_live_roster (hierarchical visibility applied).
export const teamViewAPI = {
  roster: (workspaceId) => api.get(`/team-view/${workspaceId}`),
};

// 2026-06-10 — Owner-only Attendance & Payroll API. Every endpoint here
// returns 403 for non-owners (except `holidaysPublic` which is read-only
// for any workspace member and never carries salary).
export const payrollAPI = {
  listProfiles: (workspaceId) => api.get(`/payroll/profiles/${workspaceId}`),
  getProfile: (workspaceId, userId) => api.get(`/payroll/profiles/${workspaceId}/${userId}`),
  upsertProfile: (workspaceId, userId, data) => api.put(`/payroll/profiles/${workspaceId}/${userId}`, data),
  deleteProfile: (workspaceId, userId) => api.delete(`/payroll/profiles/${workspaceId}/${userId}`),
  sheet: (workspaceId, userId, periodStart, periodEnd) =>
    api.get(`/payroll/sheet/${workspaceId}/${userId}?period_start=${periodStart}&period_end=${periodEnd}`),
  overview: (workspaceId, periodStart, periodEnd) =>
    api.get(`/payroll/overview/${workspaceId}?period_start=${periodStart}&period_end=${periodEnd}`),
  exportSheetUrl: (workspaceId, userId, periodStart, periodEnd) => {
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    return `${base}/api/payroll/sheet/${workspaceId}/${userId}/export?period_start=${periodStart}&period_end=${periodEnd}`;
  },
  holidays: (workspaceId, year, country = 'IN') =>
    api.get(`/payroll/holidays/${workspaceId}?year=${year}&country=${country}`),
  upsertHolidays: (workspaceId, year, entries, country = 'IN') =>
    api.put(`/payroll/holidays/${workspaceId}?country=${country}`, { year, entries }),
  holidaysPublic: (workspaceId, year, country = 'IN') =>
    api.get(`/payroll/holidays/${workspaceId}/public?year=${year}&country=${country}`),
  setLeavePayStatus: (workspaceId, leaveId, payStatus) =>
    api.put(`/payroll/leaves/${workspaceId}/${leaveId}/pay-status`, { pay_status: payStatus }),
};

export const webhookAPI = {
  list: (workspaceId) => api.get(`/webhooks/${workspaceId}`),
  create: (data) => api.post('/webhooks', data),
  delete: (webhookId) => api.delete(`/webhooks/${webhookId}`),
  toggle: (webhookId) => api.put(`/webhooks/${webhookId}/toggle`),
  hub: (workspaceId) => api.get(`/integrations-hub/${workspaceId}`),
};



// AI
export const aiAPI = {
  generate: (data) => api.post('/ai/generate', data),
  listThreads: (workspaceId) => api.get(`/ai/threads/${workspaceId}`),
};

// Integrations
export const integrationAPI = {
  list: (workspaceId) => api.get(`/integrations/${workspaceId}`),
  connect: (workspaceId, platform) => api.post(`/integrations/${workspaceId}/connect/${platform}`),
  add: (data) => api.post('/integrations', data),
  remove: (workspaceId, platform) => api.delete(`/integrations/${workspaceId}/${platform}`),
  connected: (workspaceId) => api.get(`/integrations/${workspaceId}/connected`),
  pages: (workspaceId) => api.get(`/integrations/${workspaceId}/pages`),
  disconnect: (workspaceId, platform) => api.post(`/integrations/${workspaceId}/disconnect/${platform}`),
};

// D86 — Facebook social CRM (multi-page selection + client group attach + publish).
export const socialAPI = {
  pending: (workspaceId) => api.get(`/social/connections/${workspaceId}/pending`),
  attach: (workspaceId, body) => api.post(`/social/connections/${workspaceId}/attach`, body),
  listConnections: (workspaceId) => api.get(`/social/connections/${workspaceId}`),
  updatePage: (workspaceId, pageId, body) => api.put(`/social/connections/${workspaceId}/page/${pageId}`, body),
  disconnectPage: (workspaceId, pageId) => api.delete(`/social/connections/${workspaceId}/page/${pageId}`),
  publishFacebook: (body) => api.post('/social/publish/facebook', body),
  // D89 / Piece B — Post Composer draft API.
  clientAccounts: (workspaceId, clientGroupId) =>
    api.get(`/social/clients/${clientGroupId}/accounts?workspace_id=${workspaceId}`),
  listPosts: (workspaceId, { status, client_group_id, skip = 0, limit = 50 } = {}) => {
    const q = new URLSearchParams({ workspace_id: workspaceId, skip, limit });
    if (status) q.set('status', status);
    if (client_group_id) q.set('client_group_id', client_group_id);
    return api.get(`/social/posts?${q.toString()}`);
  },
  getPost: (workspaceId, postId) =>
    api.get(`/social/posts/${postId}?workspace_id=${workspaceId}`),
  createPost: (body) => api.post('/social/posts', body),
  patchPost: (workspaceId, postId, body) =>
    api.patch(`/social/posts/${postId}?workspace_id=${workspaceId}`, body),
  deletePost: (workspaceId, postId) =>
    api.delete(`/social/posts/${postId}?workspace_id=${workspaceId}`),
  // D90 — Bulk-assign connected Pages to a Client Group.
  bulkAssign: (body) => api.patch('/social/accounts/bulk-assign', body),
  // D91 / Piece C — Publish + retry.
  publishPost: (workspaceId, postId) =>
    api.post(`/social/posts/${postId}/publish?workspace_id=${workspaceId}`),
  retryPost: (workspaceId, postId) =>
    api.post(`/social/posts/${postId}/retry?workspace_id=${workspaceId}`),
  // Piece E — Queue UI helpers.
  cancelSchedule: (workspaceId, postId) =>
    api.post(`/social/posts/${postId}/cancel-schedule?workspace_id=${workspaceId}`),
  schedulerStatus: () => api.get('/social/scheduler/status'),
};

// Publishing
export const publishAPI = {
  facebook: (data) => api.post('/publish/facebook', data),
  instagram: (data) => api.post('/publish/instagram', data),
  all: (workspaceId, postId, pageId) => api.post(`/publish/${workspaceId}/${postId}${pageId ? `?page_id=${pageId}` : ''}`),
};

// Ads
export const adAPI = {
  list: (workspaceId) => api.get(`/ads/${workspaceId}`),
  create: (data) => api.post('/ads', data),
  update: (campaignId, data) => api.put(`/ads/${campaignId}`, data),
  delete: (campaignId) => api.delete(`/ads/${campaignId}`),
};

// Analytics
export const analyticsAPI = {
  get: (workspaceId, days = 30) => api.get(`/analytics/${workspaceId}?days=${days}`),
  chartData: (workspaceId, days = 30) => api.get(`/analytics/${workspaceId}/chart-data?days=${days}`),
};

// Reports
export const reportAPI = {
  monthly: (workspaceId, year, month, platforms = [], groupId = '') => {
    let url = `/reports/${workspaceId}/monthly?year=${year}&month=${month}`;
    if (platforms.length > 0) url += `&platforms=${platforms.join(',')}`;
    if (groupId) url += `&group_id=${groupId}`;
    return api.get(url);
  },
};

// Client Groups
export const clientGroupAPI = {
  list: (workspaceId, { includeArchived = false, managedByMe = false } = {}) => {
    const q = new URLSearchParams();
    if (includeArchived) q.set('include_archived', 'true');
    if (managedByMe) q.set('managed_by_me', 'true');
    const qs = q.toString();
    return api.get(`/client-groups/${workspaceId}${qs ? `?${qs}` : ''}`);
  },
  get: (workspaceId, groupId) => api.get(`/client-groups/${workspaceId}/${groupId}`),
  create: (data) => api.post('/client-groups', data),
  update: (workspaceId, groupId, data) => api.put(`/client-groups/${workspaceId}/${groupId}`, data),
  delete: (workspaceId, groupId) => api.delete(`/client-groups/${workspaceId}/${groupId}`),
  deletePreview: (workspaceId, groupId) => api.get(`/client-groups/${workspaceId}/${groupId}/delete-preview`),
  restore: (workspaceId, groupId) => api.post(`/client-groups/${workspaceId}/${groupId}/restore`),
  archive: (workspaceId, groupId) => api.post(`/client-groups/${workspaceId}/${groupId}/archive`),
  unarchive: (workspaceId, groupId) => api.post(`/client-groups/${workspaceId}/${groupId}/unarchive`),
  activity: (workspaceId, groupId, limit = 100) => api.get(`/client-groups/${workspaceId}/${groupId}/activity?limit=${limit}`),
  pastCycles: (workspaceId, groupId, limit = 6) => api.get(`/client-groups/${workspaceId}/${groupId}/past-cycles?limit=${limit}`),
  uploadLogo: (workspaceId, groupId, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/client-groups/${workspaceId}/${groupId}/logo`, fd); },
  listFiles: (workspaceId, groupId) => api.get(`/client-groups/${workspaceId}/${groupId}/files`),
  uploadFile: (workspaceId, groupId, file) => { const fd = new FormData(); fd.append('file', file); return api.post(`/client-groups/${workspaceId}/${groupId}/files`, fd); },
  downloadFile: (workspaceId, mediaId) => api.get(`/media/${workspaceId}/${mediaId}/download`, { responseType: 'blob' }),
  deliverableCounts: (workspaceId, groupId) => api.get(`/client-groups/${workspaceId}/${groupId}/deliverable-counts`),
  deleteFile: (workspaceId, groupId, fileId) => api.delete(`/client-groups/${workspaceId}/${groupId}/files/${fileId}`),
  addProfile: (workspaceId, groupId, data) => api.post(`/client-groups/${workspaceId}/${groupId}/profiles`, data),
  removeProfile: (workspaceId, groupId, profileId) => api.delete(`/client-groups/${workspaceId}/${groupId}/profiles/${profileId}`),
  analytics: (workspaceId, groupId, days = 30) => api.get(`/client-groups/${workspaceId}/${groupId}/analytics?days=${days}`),
  updateMembers: (workspaceId, groupId, memberIds) => api.put(`/client-groups/${workspaceId}/${groupId}`, { assigned_members: memberIds }),
  // D60 PHASE 1 — Multi-location clients.
  listLocations: (workspaceId, groupId) => api.get(`/client-groups/${workspaceId}/${groupId}/locations`),
  createLocation: (workspaceId, groupId, data) => api.post(`/client-groups/${workspaceId}/${groupId}/locations`, data),
  updateLocation: (workspaceId, groupId, locationId, data) => api.put(`/client-groups/${workspaceId}/${groupId}/locations/${locationId}`, data),
  deleteLocation: (workspaceId, groupId, locationId) => api.delete(`/client-groups/${workspaceId}/${groupId}/locations/${locationId}`),
  locationCounts: (workspaceId) => api.get(`/clients-location-counts/${workspaceId}`),
  // D83 — Status pipeline (Ongoing / Potential / Seasonal / Done).
  // updatePipeline: single client status + order (or append to bottom of target column when order is omitted).
  updatePipeline: (workspaceId, groupId, status, pipeline_order = null) =>
    api.put(`/workspaces/${workspaceId}/client-groups/${groupId}/pipeline`, {
      status, pipeline_order,
    }),
  // reorderPipeline: batched updates after a drag — pass only the rows that need new orders.
  reorderPipeline: (workspaceId, updates) =>
    api.post(`/workspaces/${workspaceId}/client-groups/pipeline/reorder`, { updates }),
};

// D81 — Client Media Library API.
export const clientMediaAPI = {
  listFolders: (wid, gid) => api.get(`/client-media/${wid}/${gid}/folders`),
  createFolder: (wid, gid, name, parent_id = null) =>
    api.post(`/client-media/${wid}/${gid}/folders`, { name, parent_id }),
  updateFolder: (wid, gid, fid, patch) =>
    api.put(`/client-media/${wid}/${gid}/folders/${fid}`, patch),
  deleteFolder: (wid, gid, fid) =>
    api.delete(`/client-media/${wid}/${gid}/folders/${fid}`),
  listFiles: (wid, gid, params = {}) => {
    const q = new URLSearchParams();
    if (params.folder_id !== undefined && params.folder_id !== null)
      q.set('folder_id', params.folder_id);
    if (params.sort) q.set('sort', params.sort);
    if (params.filter_month) q.set('filter_month', params.filter_month);
    if (params.q) q.set('q', params.q);
    const qs = q.toString();
    return api.get(`/client-media/${wid}/${gid}/files${qs ? `?${qs}` : ''}`);
  },
  uploadFile: (wid, gid, file, folder_id = null, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    if (folder_id) fd.append('folder_id', folder_id);
    return api.post(`/client-media/${wid}/${gid}/files`, fd, {
      onUploadProgress: onProgress,
    });
  },
  updateFile: (wid, gid, fid, patch) =>
    api.put(`/client-media/${wid}/${gid}/files/${fid}`, patch),
  deleteFile: (wid, gid, fid) =>
    api.delete(`/client-media/${wid}/${gid}/files/${fid}`),
  shareFile: (wid, gid, fid, access = 'restricted') =>
    api.post(`/client-media/${wid}/${gid}/files/${fid}/share?access=${access}`),
  revokeShare: (wid, gid, fid) =>
    api.delete(`/client-media/${wid}/${gid}/files/${fid}/share`),
  // D81b — Folder sharing (Drive-style: Restricted vs Anyone-with-link).
  // Creates a single token covering the folder AND its full subtree; files
  // added later automatically inherit the share via the public folder
  // endpoint. Flipping access between 'restricted' and 'anyone' DOES NOT
  // rotate the token — already-pasted links keep working.
  shareFolder: (wid, gid, fid, access = 'restricted') =>
    api.post(`/client-media/${wid}/${gid}/folders/${fid}/share?access=${access}`),
  revokeFolderShare: (wid, gid, fid) =>
    api.delete(`/client-media/${wid}/${gid}/folders/${fid}/share`),
  // D90 — import approved task-submission files into the media library
  listApprovedSubmissionFiles: (wid, gid) =>
    api.get(`/client-media/${wid}/${gid}/approved-submission-files`),
  importFromSubmissions: (wid, gid, items, folder_id = null) =>
    api.post(`/client-media/${wid}/${gid}/import-from-submissions`, { items, folder_id }),
};



// D60 PHASE 2 — Content Calendar API.
export const contentPostAPI = {
  list: (workspaceId, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '')).toString();
    return api.get(`/content-posts/${workspaceId}${qs ? `?${qs}` : ''}`);
  },
  create: (workspaceId, body) => api.post(`/content-posts/${workspaceId}`, body),
  update: (workspaceId, postId, body) => api.put(`/content-posts/${workspaceId}/${postId}`, body),
  remove: (workspaceId, postId) => api.delete(`/content-posts/${workspaceId}/${postId}`),
  duplicate: (workspaceId, postId, locationIds) => api.post(`/content-posts/${workspaceId}/${postId}/duplicate`, { location_ids: locationIds }),
  conflicts: (workspaceId) => api.get(`/content-post-conflicts/${workspaceId}`),
  // D61 — comments + share + approve.
  listComments: (workspaceId, postId) => api.get(`/content-posts/${workspaceId}/${postId}/comments`),
  addComment: (workspaceId, postId, body, parentCommentId = null) => api.post(`/content-posts/${workspaceId}/${postId}/comments`, { body, parent_comment_id: parentCommentId }),
  share: (workspaceId, postId) => api.post(`/content-posts/${workspaceId}/${postId}/share`),
  // D63 — comment-read tracking for violet calendar tint.
  markCommentsRead: (workspaceId, postId) => api.post(`/content-posts/${workspaceId}/${postId}/comments/mark-read`),
  unreadCommentsSummary: (workspaceId, clientId) => api.get(`/content-posts/${workspaceId}/unread-comments-summary${clientId ? `?client_id=${clientId}` : ''}`),
};

// D61 — Public content-review share page (no auth).
export const publicContentPostAPI = {
  get: (token) => api.get(`/public/posts/${token}`),
  addComment: (token, payload) => api.post(`/public/posts/${token}/comments`, payload),
  approve: (token, payload) => api.post(`/public/posts/${token}/approve`, payload),
};

// D62 — Client contacts (reusable; used in Send Calendar modal + Contacts tab).
export const clientContactAPI = {
  list: (workspaceId, clientId) => api.get(`/client-contacts/${workspaceId}/${clientId}`),
  create: (workspaceId, clientId, payload) => api.post(`/client-contacts/${workspaceId}/${clientId}`, payload),
  update: (workspaceId, clientId, contactId, payload) => api.put(`/client-contacts/${workspaceId}/${clientId}/${contactId}`, payload),
  remove: (workspaceId, clientId, contactId) => api.delete(`/client-contacts/${workspaceId}/${clientId}/${contactId}`),
};

// D62 — Month-scoped calendar share for clients.
export const calendarShareAPI = {
  create: (workspaceId, payload) => api.post(`/content-calendar-shares/${workspaceId}`, payload),
  listForClient: (workspaceId, clientId) => api.get(`/content-calendar-shares/${workspaceId}/${clientId}`),
  resend: (workspaceId, shareId) => api.post(`/content-calendar-shares/${workspaceId}/${shareId}/resend`),
};

export const publicCalendarShareAPI = {
  get: (token) => api.get(`/public/calendar/${token}`),
  approveAll: (token, payload) => api.post(`/public/calendar/${token}/approve-all`, payload),
};

// D63 Issue 5 — Sidebar live count badges.
export const sidebarAPI = {
  counts: (workspaceId) => api.get(`/sidebar/counts${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
};

// Idea Bank — parking lot for tasks without a deadline that aren't being
// actively worked on. Items NEVER show up in /tasks, calendar, EOD reports,
// or overdue alerts. The user must "Activate" an item to convert it into
// a real task (the activate endpoint just returns the prefill payload — the
// frontend then POSTs /tasks and DELETEs the idea bank item).
export const ideaBankAPI = {
  list: (workspaceId) => api.get(`/idea-bank/${workspaceId}`),
  categories: (workspaceId) => api.get(`/idea-bank/${workspaceId}/categories`),
  count: (workspaceId) => api.get(`/idea-bank/${workspaceId}/count`),
  create: (data) => api.post('/idea-bank', data),
  update: (workspaceId, itemId, data) => api.put(`/idea-bank/${workspaceId}/${itemId}`, data),
  delete: (workspaceId, itemId) => api.delete(`/idea-bank/${workspaceId}/${itemId}`),
  activate: (workspaceId, itemId) => api.post(`/idea-bank/${workspaceId}/${itemId}/activate`),
  deleteAttachment: (workspaceId, itemId, attachmentId) => api.delete(`/idea-bank/${workspaceId}/${itemId}/attachments/${attachmentId}`),
};

// 2026-06-16 — Task attachments (delete; uploads use /api/media/upload directly).
export const taskAttachmentAPI = {
  delete: (workspaceId, taskId, attachmentId) => api.delete(`/tasks/${workspaceId}/${taskId}/attachments/${attachmentId}`),
};

// ──────────────────────────────────────────────────────────────────────
// Batch 4 — Supervisor Tree, EOD v2, Clock-in v2, Admin OP-1/OP-2.
// Backend verified end-to-end; frontend UI is a follow-up batch.
// ──────────────────────────────────────────────────────────────────────
export const supervisorTreeAPI = {
  list: (workspaceId) => api.get(`/supervisor-tree?workspace_id=${workspaceId}`),
  reports: (workspaceId, userId) => api.get(`/supervisor-tree/reports/${userId}?workspace_id=${workspaceId}`),
  supervisors: (workspaceId, userId) => api.get(`/supervisor-tree/supervisors/${userId}?workspace_id=${workspaceId}`),
  create: (workspaceId, supervisorId, reportId) => api.post('/supervisor-tree', { workspace_id: workspaceId, supervisor_user_id: supervisorId, report_user_id: reportId }),
  remove: (workspaceId, assignmentId) => api.delete(`/supervisor-tree/${assignmentId}?workspace_id=${workspaceId}`),
};

export const eodAPI = {
  submit: (workspaceId, body) => api.post('/eod', { workspace_id: workspaceId, ...body }),
  update: (eodId, body) => api.patch(`/eod/${eodId}`, body),
  list: (workspaceId, userId, fromDate = '', toDate = '') => {
    const q = new URLSearchParams({ workspace_id: workspaceId, user_id: userId });
    if (fromDate) q.set('from_date', fromDate);
    if (toDate) q.set('to_date', toDate);
    return api.get(`/eod?${q.toString()}`);
  },
  calendar: (workspaceId, weekStart) => api.get(`/eod/calendar?workspace_id=${workspaceId}&week_start=${weekStart}`),
};

export const clockV2API = {
  status: (workspaceId) => api.get(`/clock/v2/status?workspace_id=${workspaceId}`),
  heartbeat: (workspaceId) => api.get(`/clock/v2/heartbeat?workspace_id=${workspaceId}`),
  in: (workspaceId, notes = '', loc = null) => api.post('/clock/v2/in', {
    workspace_id: workspaceId, notes,
    lat: loc?.lat ?? null, lng: loc?.lng ?? null,
    accuracy: loc?.accuracy ?? null, address: loc?.address ?? null,
  }),
  out: (workspaceId, loc = null) => api.post(
    `/clock/v2/out?workspace_id=${workspaceId}`,
    loc ? {
      workspace_id: workspaceId,
      lat: loc.lat, lng: loc.lng,
      accuracy: loc.accuracy ?? null, address: loc.address ?? null,
    } : { workspace_id: workspaceId },
  ),
  todaySummary: (workspaceId, userId = null) => {
    const u = userId ? `&user_id=${encodeURIComponent(userId)}` : '';
    return api.get(`/clock/v2/today-summary?workspace_id=${workspaceId}${u}`);
  },
  notWorkingToday: (workspaceId, reason = '') => api.post('/clock/not-working-today', {
    workspace_id: workspaceId, reason,
  }),
  getClockInWindow: (workspaceId) => api.get(`/workspaces/${workspaceId}/settings/clock-in-window`),
  setClockInWindow: (workspaceId, start, end) => api.put(`/workspaces/${workspaceId}/settings/clock-in-window`, { start, end }),
};

export const pushAPI = {
  vapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (subscription, workspaceId) => api.post('/push/subscribe', {
    endpoint: subscription.endpoint,
    keys: { p256dh: subscription.keys.p256dh, auth: subscription.keys.auth },
    expirationTime: subscription.expirationTime ?? null,
    user_agent: navigator.userAgent || '',
    workspace_id: workspaceId || null,
  }),
  unsubscribe: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
  sendTest: () => api.post('/push/send-test'),
};

// 2026-06-16 — Team Pulse presence. Ping every time a user opens / closes
// a task so the dashboard "Working on" label is accurate within 30 s.
export const presenceAPI = {
  viewingTask: (workspaceId, taskId) => api.post('/presence/viewing-task', {
    workspace_id: workspaceId,
    task_id: taskId || null,
  }),
};

export const adminOpsAPI = {
  backfillActivityClientId: (workspaceId, { confirm = false } = {}) =>
    api.post(`/admin/backfill-activity-client-id?workspace_id=${workspaceId}${confirm ? '&confirm=true' : ''}`),
  seedAuditTestClient2B3: (workspaceId) =>
    api.post(`/admin/seed-audit-test-client-2b3?workspace_id=${workspaceId}`),
};


// Digest
export const digestAPI = {
  sendWeekly: (workspaceId) => api.post(`/digest/send-weekly/${workspaceId}`),
  sendAiDaily: (workspaceId) => api.post(`/digest/ai-daily/${workspaceId}`),
  previewAiDaily: (workspaceId) => api.get(`/digest/ai-daily/${workspaceId}/preview`),
  // 2026-02-22 — New tracked team report send + owner-only Digest Log.
  sendTeamReport: (workspaceId, timeRangeDays) =>
    api.post(`/digest/send-team-report/${workspaceId}`, { time_range_days: timeRangeDays }),
  getLog: (workspaceId, opts = {}) =>
    api.get(`/digest/log`, { params: { workspace_id: workspaceId, ...opts } }),
  // 2026-07-15 — retry a single failed/suppressed digest delivery (owner only).
  retryDelivery: (workspaceId, deliveryId) => api.post(`/digest/retry/${workspaceId}/${deliveryId}`),
  resendNonOpeners: (workspaceId, opts = {}) =>
    api.post(`/digest/resend-non-openers/${workspaceId}`, opts),
};

// 2026-02-22 — AI Standup endpoint (per-member EOD rollup).
export const aiStandupAPI = {
  get: (userId, workspaceId, days = 7, refresh = false) =>
    api.get(`/eod/team-member/${userId}/ai-standup`, {
      params: { workspace_id: workspaceId, days, refresh },
    }),
};

// Weekly Standup
export const standupAPI = {
  generate: (workspaceId) => api.get(`/standup/${workspaceId}/generate`),
  downloadPdf: (workspaceId) => api.get(`/standup/${workspaceId}/pdf`, { responseType: 'blob' }),
  sendEmail: (workspaceId) => api.post(`/standup/${workspaceId}/send`),
};

// Project Sharing
export const shareAPI = {
  createProjectLink: (workspaceId, projectId) => api.post(`/share/project/${workspaceId}/${projectId}`),
  revokeProjectLink: (workspaceId, projectId) => api.delete(`/share/project/${workspaceId}/${projectId}`),
};

// Saved Views
export const viewsAPI = {
  list: (workspaceId) => api.get(`/views/${workspaceId}`),
  create: (workspaceId, data) => api.post(`/views/${workspaceId}`, data),
  delete: (workspaceId, viewId) => api.delete(`/views/${workspaceId}/${viewId}`),
};

// Pages (Notion-style)
export const pageAPI = {
  list: (workspaceId) => api.get(`/pages/${workspaceId}`),
  get: (workspaceId, pageId) => api.get(`/pages/${workspaceId}/${pageId}`),
  create: (data) => api.post('/pages', data),
  update: (workspaceId, pageId, data) => api.put(`/pages/${workspaceId}/${pageId}`, data),
  delete: (workspaceId, pageId) => api.delete(`/pages/${workspaceId}/${pageId}`),
};

// Projects
export const projectAPI = {
  list: (workspaceId) => api.get(`/projects/${workspaceId}`),
  get: (workspaceId, projectId) => api.get(`/projects/${workspaceId}/${projectId}`),
  create: (data) => api.post('/projects', data),
  update: (workspaceId, projectId, data) => api.put(`/projects/${workspaceId}/${projectId}`, data),
  delete: (workspaceId, projectId) => api.delete(`/projects/${workspaceId}/${projectId}`),
};

// Tasks
export const taskAPI = {
  // 2026-09 — global task search (title/client/people/description/id).
  searchTasks: (workspaceId, q) => api.get(`/tasks/${workspaceId}/search-tasks`, { params: { q } }),
  // Batch 5 R2 F1 — visibility-debug QA endpoint (Owner/Admin only).
  visibilityDebug: (workspaceId, taskId, targetUserId = null) => {
    const q = new URLSearchParams({ workspace_id: workspaceId });
    if (targetUserId) q.set('target_user_id', targetUserId);
    return api.get(`/tasks/visibility-debug/${taskId}?${q.toString()}`);
  },
  list: (workspaceId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters.priority) params.append('priority', filters.priority);
    // BUG-C02/C03/C04 — forward client_group_id so the client detail page
    // can fetch only the client's tasks (was silently dropped, causing
    // every client to render the same global first-page tasks).
    if (filters.client_group_id) params.append('client_group_id', filters.client_group_id);
    if (filters.parent_only !== undefined) params.append('parent_only', filters.parent_only);
    if (filters.cursor) params.append('cursor', filters.cursor);
    if (filters.cursor_id) params.append('cursor_id', filters.cursor_id);
    if (filters.limit) params.append('limit', filters.limit);
    return api.get(`/tasks/${workspaceId}?${params.toString()}`).then(res => {
      // Normalize response — handle both old flat array and new paginated format
      if (Array.isArray(res.data)) return res;
      return { ...res, data: res.data.tasks || [], _pagination: { next_cursor: res.data.next_cursor, next_cursor_id: res.data.next_cursor_id, total: res.data.total } };
    });
  },
  get: (workspaceId, taskId) => api.get(`/tasks/${workspaceId}/${taskId}`),
  create: (data) => api.post('/tasks', data),
  update: (workspaceId, taskId, data) => api.put(`/tasks/${workspaceId}/${taskId}`, data),
  delete: (workspaceId, taskId) => api.delete(`/tasks/${workspaceId}/${taskId}`),
  dashboard: (workspaceId) => api.get(`/tasks/dashboard/${workspaceId}`),
  logTime: (data) => api.post('/tasks/time-entry', data),
  timeEntries: (workspaceId, userId, days = 30) => api.get(`/tasks/time-entries/${workspaceId}?${userId ? `user_id=${userId}&` : ''}days=${days}`),
  addComment: (workspaceId, taskId, text, attachments = null, submissionId = null) =>
    api.post(`/tasks/${workspaceId}/${taskId}/comments`, {
      task_id: taskId,
      workspace_id: workspaceId,
      text,
      attachments: attachments || undefined,
      submission_id: submissionId || undefined,
    }),
  // D87 Feature 2 — rich comment upload (image / voice / video / document).
  uploadCommentAttachment: (workspaceId, taskId, file, { kind = '', text = '', submissionId = '' } = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (kind) form.append('kind', kind);
    if (text) form.append('text', text);
    if (submissionId) form.append('submission_id', submissionId);
    return api.post(`/tasks/${workspaceId}/${taskId}/comments/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // 2026-07-13 — version-thread rich composer: attachments + voice notes
  // post into the SUBMISSION comment thread (feedback tied to the version).
  uploadSubmissionCommentAttachment: (workspaceId, taskId, submissionId, file, { kind = '', text = '', inReplyTo = '' } = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (kind) form.append('kind', kind);
    if (text) form.append('text', text);
    if (inReplyTo) form.append('in_reply_to', inReplyTo);
    return api.post(`/tasks/${workspaceId}/${taskId}/submissions/${submissionId}/comments/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // D87 Feature 1 — Task transfer mutual-agreement flow.
  requestTransfer: (workspaceId, taskId, toUserId, reason) =>
    api.post(`/tasks/${workspaceId}/${taskId}/transfer/request`, { to_user_id: toUserId, reason }),
  acceptTransfer: (workspaceId, taskId, transferId) =>
    api.post(`/tasks/${workspaceId}/${taskId}/transfer/${transferId}/accept`),
  declineTransfer: (workspaceId, taskId, transferId, declineNote = '') =>
    api.post(`/tasks/${workspaceId}/${taskId}/transfer/${transferId}/decline`, { decline_note: declineNote }),
  // D87 add-on — list tasks where the user is in followers[] (was a prior
  // assignee, retains view + comment rights but no edit/status/reassign).
  listFollowing: (workspaceId, limit = 200) =>
    api.get(`/tasks/${workspaceId}/following`, { params: { limit } }),
  listTransfers: (workspaceId, taskId) =>
    api.get(`/tasks/${workspaceId}/${taskId}/transfers`),
  // Legacy subtasks (2026-07 collapse) — read + finish only.
  legacySubtasks: (workspaceId, taskId) =>
    api.get(`/tasks/${workspaceId}/${taskId}/legacy-subtasks`),
  generateSample: (workspaceId) => api.post(`/tasks/generate-sample/${workspaceId}`),
  aiAssist: (data) => api.post('/ai/task-assist', data),
  workload: (workspaceId) => api.get(`/tasks/workload/${workspaceId}`),
  pauseTimer: (workspaceId, taskId) => api.post(`/tasks/${workspaceId}/${taskId}/pause-timer`),
  resumeTimer: (workspaceId, taskId) => api.post(`/tasks/${workspaceId}/${taskId}/resume-timer`),
  assignedByMe: (workspaceId) => api.get(`/tasks/${workspaceId}/assigned-by-me`),
};

// Permissions
export const permissionAPI = {
  get: (workspaceId) => api.get(`/permissions/${workspaceId}`),
  update: (workspaceId, role, permissions) => api.put(`/permissions/${workspaceId}`, { role, permissions }),
};

// Activity Feed
export const activityAPI = {
  // FEATURE 6 (2026-06-04) — Optional user/role filter params (Owner/Admin/Manager only).
  list: (workspaceId, limit = 30, opts = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (opts.userId) params.set('user_id', opts.userId);
    if (opts.role) params.set('role', opts.role);
    return api.get(`/activity/${workspaceId}?${params.toString()}`);
  },
};

// Private Notes
export const noteAPI = {
  list: (workspaceId) => api.get(`/notes/${workspaceId}`),
  // D74 — notes that other members have shared with the current user
  listShared: (workspaceId) => api.get(`/notes/${workspaceId}/shared`),
  get: (workspaceId, noteId) => api.get(`/notes/${workspaceId}/${noteId}`),
  create: (data) => api.post('/notes', data),
  update: (workspaceId, noteId, data) => api.put(`/notes/${workspaceId}/${noteId}`, data),
  delete: (workspaceId, noteId) => api.delete(`/notes/${workspaceId}/${noteId}`),
  // D74 — replace the share list (array of user_ids)
  setShare: (workspaceId, noteId, sharedWith) =>
    api.put(`/notes/${workspaceId}/${noteId}/share`, { shared_with: sharedWith }),
  transcribe: (data) => api.post('/notes/ai-transcribe', data),
  audioTranscribe: (formData) => api.post('/notes/audio-transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// D75 — AI Meeting Notes (Note Taker)
export const meetingNotesAPI = {
  list: () => api.get('/meeting-notes'),
  get: (id) => api.get(`/meeting-notes/${id}`),
  status: (id) => api.get(`/meeting-notes/${id}/status`),
  upload: (formData, onUploadProgress) => api.post(
    '/meeting-notes/upload-and-process',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      // 5-minute wait for the initial PUT to object storage; the rest is async.
      timeout: 5 * 60 * 1000,
    },
  ),
  update: (id, payload) => api.patch(`/meeting-notes/${id}`, payload),
  delete: (id) => api.delete(`/meeting-notes/${id}`),
  // D75 BUG-2 — re-run transcription on a failed/stuck note.
  retry: (id) => api.post(`/meeting-notes/${id}/retry`),
  // D74 — replace the share list (array of user_ids). Empty list returns
  // the meeting note to team-collaborative default.
  setShare: (id, sharedWith) =>
    api.put(`/meeting-notes/${id}/share`, { shared_with: sharedWith }),
  audioUrl: (id) => `${api.defaults.baseURL}/meeting-notes/${id}/audio`,
};

// Client Pages
export const clientPageAPI = {
  list: (workspaceId, clientGroupId) => api.get(`/client-pages/${workspaceId}/${clientGroupId}`),
  get: (workspaceId, clientGroupId, pageId) => api.get(`/client-pages/${workspaceId}/${clientGroupId}/${pageId}`),
  create: (data) => api.post('/client-pages', data),
  update: (workspaceId, pageId, data) => api.put(`/client-pages/${workspaceId}/${pageId}`, data),
  delete: (workspaceId, pageId) => api.delete(`/client-pages/${workspaceId}/${pageId}`),
  // D74 — Replace the team-share list (array of user_ids). Creator-only.
  setShare: (workspaceId, pageId, sharedWith) =>
    api.put(`/client-pages/${workspaceId}/${pageId}/share`, { shared_with: sharedWith }),
};

// Dashboard Cards
export const dashboardCardAPI = {
  list: (workspaceId) => api.get(`/dashboard-cards/${workspaceId}`),
  add: (workspaceId, data) => api.post(`/dashboard-cards/${workspaceId}`, data),
  remove: (workspaceId, cardId) => api.delete(`/dashboard-cards/${workspaceId}/${cardId}`),
  reorder: (workspaceId, cards) => api.put(`/dashboard-cards/${workspaceId}/reorder`, cards),
  templates: () => api.get('/dashboard-cards/templates/list'),
  cardData: (workspaceId, cardType, days) => api.get(`/dashboard-cards/${workspaceId}/data/${cardType}?days=${days || 7}`),
  // Batch 5 R2 F4 — Widget Gallery v2
  gallery: (workspaceId) => api.get(`/widget-gallery?workspace_id=${workspaceId}`),
  bulkAddWidgets: (workspaceId, widgetIds) => api.post('/dashboard/widgets/bulk-add', { workspace_id: workspaceId, widget_ids: widgetIds }),
};

// Batch 5 R3 — Brand Kit (CT-013 flagship)
export const brandKitAPI = {
  get: (clientId, workspaceId) => api.get(`/clients/${clientId}/brand-kit?workspace_id=${workspaceId}`),
  patch: (clientId, workspaceId, body) => api.put(`/clients/${clientId}/brand-kit?workspace_id=${workspaceId}`, body),
  uploadAsset: (clientId, workspaceId, kind, file, family = '') => {
    const fd = new FormData();
    fd.append('file', file);
    const q = new URLSearchParams({ workspace_id: workspaceId, kind });
    if (family) q.set('family', family);
    return api.post(`/clients/${clientId}/brand-kit/assets?${q.toString()}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAsset: (clientId, workspaceId, kind, fontId = '') => {
    const q = new URLSearchParams({ workspace_id: workspaceId });
    if (fontId) q.set('font_id', fontId);
    return api.delete(`/clients/${clientId}/brand-kit/assets/${kind}?${q.toString()}`);
  },
  assetUrl: (clientId, workspaceId, kind, fontId = '') => {
    // Used inside <img src> / <a href> — must include the JWT? No — auth is via the
    // axios `api` instance for fetch-based access. For <img>, use the dedicated
    // assetBlob() helper below which returns an Object URL.
    const q = new URLSearchParams({ workspace_id: workspaceId });
    if (fontId) q.set('font_id', fontId);
    return `${api.defaults.baseURL}/clients/${clientId}/brand-kit/assets/${kind}?${q.toString()}`;
  },
  assetBlob: async (clientId, workspaceId, kind, fontId = '') => {
    const q = new URLSearchParams({ workspace_id: workspaceId });
    if (fontId) q.set('font_id', fontId);
    const res = await api.get(`/clients/${clientId}/brand-kit/assets/${kind}?${q.toString()}`, { responseType: 'blob' });
    return URL.createObjectURL(res.data);
  },
  downloadZipUrl: (clientId, workspaceId) => `${api.defaults.baseURL}/clients/${clientId}/brand-kit/zip?workspace_id=${workspaceId}`,
  downloadZip: async (clientId, workspaceId) => {
    const res = await api.get(`/clients/${clientId}/brand-kit/zip?workspace_id=${workspaceId}`, { responseType: 'blob' });
    return res.data;
  },
};

// Batch 5 R3 F2 — Person Detail Page
export const personAPI = {
  detail: (userId, workspaceId) => api.get(`/people/${userId}?workspace_id=${workspaceId}`),
  timeLog: (userId, workspaceId, limit = 50) => api.get(`/people/${userId}/time-log?workspace_id=${workspaceId}&limit=${limit}`),
  activity: (userId, workspaceId, limit = 50) => api.get(`/people/${userId}/activity?workspace_id=${workspaceId}&limit=${limit}`),
};

// Batch 5 R4 F3 — Velocity Engine
export const velocityAPI = {
  user: (userId, workspaceId) => api.get(`/velocity/user/${userId}?workspace_id=${workspaceId}`),
  team: (workspaceId) => api.get(`/velocity/team?workspace_id=${workspaceId}`),
  onTimeRate: (workspaceId, userId = null) => api.get(`/velocity/on-time-rate?workspace_id=${workspaceId}${userId ? `&user_id=${userId}` : ''}`),
  manualBatch: (payload) => api.post('/velocity/batch', payload),
};

// Batch 5 D48 BUG-09 — Reviews Queue
export const reviewsAPI = {
  queue: (workspaceId, scope = 'mine', limit = 100) => api.get(`/reviews/queue?workspace_id=${workspaceId}&scope=${scope}&limit=${limit}`),
  count: (workspaceId) => api.get(`/reviews/count?workspace_id=${workspaceId}`),
};

// Meta Pixel
export const metaPixelAPI = {
  status: (workspaceId) => api.get(`/meta-pixel/${workspaceId}/status`),
  connect: (workspaceId, data) => api.post(`/meta-pixel/${workspaceId}/connect`, data),
  analytics: (workspaceId, days = 30) => api.get(`/meta-pixel/${workspaceId}/analytics?days=${days}`),
};

// Settings
export const settingsAPI = {
  emailConfig: () => api.get('/settings/email-config'),
};

// Credentials
export const credentialsAPI = {
  getSmtp: (workspaceId) => api.get(`/credentials/${workspaceId}/smtp`),
  saveSmtp: (workspaceId, data) => api.post(`/credentials/${workspaceId}/smtp`, data),
  getPlatforms: (workspaceId) => api.get(`/credentials/${workspaceId}/platforms`),
  savePlatform: (workspaceId, data) => api.post(`/credentials/${workspaceId}/platform`, data),
  deletePlatform: (workspaceId, platform) => api.delete(`/credentials/${workspaceId}/platform/${platform}`),
};

// Admin (Super Admin only)
export const adminAPI = {
  listUsers: (status) => api.get(`/admin/users${status ? `?status=${status}` : ''}`),
  pendingCount: () => api.get('/admin/users/pending-count'),
  approve: (userId, data = {}) => api.post(`/admin/users/${userId}/approve`, data),
  decline: (userId) => api.post(`/admin/users/${userId}/decline`),
  deactivate: (userId) => api.post(`/admin/users/${userId}/deactivate`),
  reactivate: (userId) => api.post(`/admin/users/${userId}/reactivate`),
  changeRole: (userId, data) => api.post(`/admin/users/${userId}/change-role`, data),
  listWorkspaces: () => api.get('/admin/workspaces'),
};

// Users Table (Task Manager)
export const usersTableAPI = {
  list: (workspaceId) => api.get(`/users/table/${workspaceId}`),
  // D56 PART A — Org-chart-scoped EOD-visible members.
  listEodVisible: (workspaceId) => api.get(`/eod/members?workspace_id=${workspaceId}`),
};

// Dashboard
export const dashboardAPI = {
  get: (workspaceId) => api.get(`/dashboard/${workspaceId}`),
  downloadPdf: (workspaceId, days = 30) => api.get(`/reports/${workspaceId}/dashboard-pdf?days=${days}`, { responseType: 'blob' }),
};

// Notifications
export const notificationAPI = {
  list: (limit = 20) => api.get(`/notifications?limit=${limit}`),
  // Notifications v2 (2026-06-04) — important-only feed for the dashboard widget.
  listImportant: (limit = 10) => api.get(`/notifications/important?limit=${limit}`),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  discard: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear/all'),
};

// Activity Events
export const activityEventsAPI = {
  userActivity: (userId, days, eventType, taskId) => {
    let url = `/users/${userId}/activity?days=${days || 1}`;
    if (eventType) url += `&event_type=${eventType}`;
    if (taskId) url += `&task_id=${taskId}`;
    return api.get(url);
  },
  visibleUsers: (workspaceId) => api.get(`/activity/visible-users/${workspaceId}`),
};

// Leave Management
export const leaveAPI = {
  list: (workspaceId, status) => api.get(`/leave-requests/${workspaceId}${status ? '?status=' + status : ''}`),
  pending: (workspaceId) => api.get(`/leave-requests/${workspaceId}/pending`),
  my: (workspaceId) => api.get(`/leave-requests/${workspaceId}/my`),
  submit: (workspaceId, data) => api.post(`/leave-requests/${workspaceId}`, data),
  vote: (workspaceId, leaveId, vote) => api.post(`/leave-requests/${workspaceId}/${leaveId}/vote`, { vote }),
  cancel: (workspaceId, leaveId) => api.delete(`/leave-requests/${workspaceId}/${leaveId}`),
  calendar: (workspaceId, month) => api.get(`/leave-requests/${workspaceId}/calendar${month ? '?month=' + month : ''}`),
};

// Chat
export const chatAPI = {
  channels: (workspaceId) => api.get(`/chat/${workspaceId}/channels`),
  createChannel: (workspaceId, data) => api.post(`/chat/${workspaceId}/channels`, data),
  messages: (workspaceId, channelId, skip = 0) => api.get(`/chat/${workspaceId}/channels/${channelId}/messages?skip=${skip}&limit=50`),
  // Phase 3A Follow-up — accepts either a plain string (legacy) or a full
  // payload object with mention metadata (`text`, `mention_user_ids`,
  // `mention_channel`, `mention_here`).
  sendMessage: (workspaceId, channelId, textOrPayload) => {
    const payload = typeof textOrPayload === 'string'
      ? { text: textOrPayload }
      : textOrPayload;
    return api.post(`/chat/${workspaceId}/channels/${channelId}/messages`, payload);
  },
  markRead: (workspaceId, channelId) => api.put(`/chat/${workspaceId}/channels/${channelId}/read`),
  online: (workspaceId) => api.get(`/chat/${workspaceId}/online`),
  updateChannel: (workspaceId, channelId, data) => api.put(`/chat/${workspaceId}/channels/${channelId}`, data),
  deleteChannel: (workspaceId, channelId) => api.delete(`/chat/${workspaceId}/channels/${channelId}`),
  // D77 — Upload a voice/image/video/document attachment. `formData` must
  // include `file`. Optional fields: `kind`, `text`, `reply_to_message_id`.
  uploadAttachment: (workspaceId, channelId, formData, onUploadProgress) => api.post(
    `/chat/${workspaceId}/channels/${channelId}/messages/upload`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      // 100 MB blobs over slow links can take a while.
      timeout: 5 * 60 * 1000,
    },
  ),
  attachmentUrl: (workspaceId, channelId, messageId) =>
    `${api.defaults.baseURL}/chat/${workspaceId}/channels/${channelId}/messages/${messageId}/file`,
  // D77 — Per-message delete. mode = 'me' | 'everyone'.
  deleteMessage: (workspaceId, channelId, messageId, mode) =>
    api.delete(`/chat/${workspaceId}/channels/${channelId}/messages/${messageId}?mode=${encodeURIComponent(mode)}`),
  // D77b — Toggle a reaction on a message (idempotent).
  reactMessage: (workspaceId, channelId, messageId, emoji) =>
    api.post(`/chat/${workspaceId}/channels/${channelId}/messages/${messageId}/react`,
      null, { params: { emoji } }),
};

// Iter 69 — Contracts (Owner-only)
export const contractsAPI = {
  options: () => api.get('/contracts/options'),
  list: (workspaceId, opts = {}) => {
    const params = [];
    if (opts.status) params.push(`status=${encodeURIComponent(opts.status)}`);
    if (opts.archived) params.push(`archived=${encodeURIComponent(opts.archived)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return api.get(`/contracts/${workspaceId}${qs}`);
  },
  create: (workspaceId, payload) => api.post(`/contracts/${workspaceId}`, payload),
  get: (workspaceId, contractId) => api.get(`/contracts/${workspaceId}/${contractId}`),
  update: (workspaceId, contractId, payload) => api.put(`/contracts/${workspaceId}/${contractId}`, payload),
  // D73 — DELETE now soft-archives (moves to Trash). Use permanentDelete for hard-delete.
  archive: (workspaceId, contractId) => api.delete(`/contracts/${workspaceId}/${contractId}`),
  restore: (workspaceId, contractId) => api.post(`/contracts/${workspaceId}/${contractId}/restore`),
  permanentDelete: (workspaceId, contractId) => api.delete(`/contracts/${workspaceId}/${contractId}/permanent`),
  // Legacy alias kept temporarily — points at the new soft-archive endpoint.
  remove: (workspaceId, contractId) => api.delete(`/contracts/${workspaceId}/${contractId}`),
  generatePdf: (workspaceId, contractId) => api.post(`/contracts/${workspaceId}/${contractId}/generate-pdf`),
  pdfUrl: (workspaceId, contractId, type = 'original') =>
    `${API_BASE}/contracts/${workspaceId}/${contractId}/pdf?type=${type}`,
  send: (workspaceId, contractId, payload = {}) =>
    api.post(`/contracts/${workspaceId}/${contractId}/send`, payload),
  // D71 — manual reminder resend (owner only). Rotates the tracking token.
  resendReminder: (workspaceId, contractId, payload = {}) =>
    api.post(`/contracts/${workspaceId}/${contractId}/resend-reminder`, payload),
  markSigned: (workspaceId, contractId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/contracts/${workspaceId}/${contractId}/mark-signed`, fd);
  },
};

// Iter 69.2 — Contract Templates (Owner-only)
export const contractTemplatesAPI = {
  list: () => api.get('/contracts/templates'),
  get: (templateId) => api.get(`/contracts/templates/${templateId}`),
  saveDraft: (templateId, payload) =>
    api.put(`/contracts/templates/${templateId}/draft`, payload),
  publish: (templateId) =>
    api.post(`/contracts/templates/${templateId}/publish`),
  reset: (templateId) =>
    api.post(`/contracts/templates/${templateId}/reset`),
  preview: (templateId, payload) =>
    api.post(`/contracts/templates/${templateId}/preview`, payload, { responseType: 'blob' }),
};

export default api;

// Media URLs are minted server-side with the APP_URL at upload time; after a
// fork/redeploy that origin can go stale. Rewrite any /api/uploads/ URL onto
// the current canonical backend origin so old files keep rendering.
export const toCurrentOrigin = (url) => {
  if (!url) return url;
  const i = url.indexOf('/api/uploads/');
  if (i === -1) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url.slice(i)}`;
};
